import Navbar from "@/components/layout/Navbar";
import { Button } from "@/components/ui/button";
import ScrollAnimation from "@/components/ui/ScrollAnimation";
import {
  ArrowRight,
  CheckCircle2,
  MessageSquare,
  Sparkles,
  Target,
  TrendingUp,
  Users,
  Zap
} from "lucide-react";
import { Link } from "react-router-dom";

const Index = () => {
  const features = [
    {
      icon: MessageSquare,
      title: "AI-Powered Chat",
      description: "Natural language interface to query priorities, analyze reports, and get intelligent insights instantly.",
    },
    {
      icon: Target,
      title: "Priority Management",
      description: "Track Shippable (SHP) and Weekly Key Results (WKR) with precision and clarity.",
    },
    {
      icon: Users,
      title: "Team Coordination",
      description: "Seamlessly organize employees into teams with clear manager-staff relationships and assignments.",
    },
  ];

  const benefits = [
    "Context-aware AI that understands your priorities",
    "Role-based access for robust security",
    "Automated daily reporting & blocker tracking",
    "Real-time team collaboration & sync",
  ];



  return (
    <div className="min-h-screen bg-background font-sans selection:bg-primary/20">
      <Navbar />

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-32 pb-20 lg:pt-40 lg:pb-24">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-background to-background pointer-events-none" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 h-[600px] w-[600px] bg-primary/20 blur-[120px] rounded-full pointer-events-none opacity-40 animate-pulse" />
        <div className="absolute bottom-0 right-0 h-[400px] w-[400px] bg-accent/10 blur-[100px] rounded-full pointer-events-none opacity-30" />

        <div className="container relative mx-auto px-4">
          <div className="mx-auto max-w-5xl text-center">
            <ScrollAnimation animation="fade-in">
              <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-4 py-1.5 backdrop-blur-sm shadow-sm hover:bg-primary/10 transition-colors cursor-default">
                <Sparkles className="h-3.5 w-3.5 text-primary animate-pulse" />
                <span className="text-sm font-medium text-foreground/80">The Future of Work Management</span>
              </div>
            </ScrollAnimation>

            <ScrollAnimation delay={0.1}>
              <h1 className="mb-8 text-5xl font-bold tracking-tight sm:text-7xl lg:text-8xl leading-[1.05] filter drop-shadow-sm">
                Help Your Team Do the
                <br />
                <span className="text-gradient font-extrabold relative inline-block animate-shimmer-text">
                  Right Work
                  <span className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-primary/0 via-primary/50 to-primary/0 rounded-full blur-sm opacity-50" />
                </span>, Not Do More Work.
              </h1>
            </ScrollAnimation>

            <ScrollAnimation delay={0.2}>
              <p className="mx-auto mb-12 max-w-2xl text-xl sm:text-2xl text-muted-foreground leading-relaxed font-medium">
                Work on What Matters Most! <span className="text-foreground font-semibold">Every Single Day.</span>
              </p>
            </ScrollAnimation>

            <ScrollAnimation delay={0.3}>
              <div className="flex flex-col items-center justify-center gap-6 sm:flex-row">
                <Link to="/auth">
                  <Button variant="hero" size="xl" className="shadow-glow transition-all duration-300 hover:shadow-glow-sm hover:-translate-y-0.5 text-lg h-14 px-8">
                    Get Started
                    <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/auth">
                  <Button variant="outline" size="xl" className="bg-background/50 backdrop-blur-sm border-primary/20 hover:bg-primary/5 text-lg h-14 px-8">
                    Sign In to Dashboard
                  </Button>
                </Link>
              </div>
            </ScrollAnimation>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-background relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-[0.02] pointer-events-none" />
        <div className="container mx-auto px-4 relative">
          <ScrollAnimation>
            <div className="mx-auto max-w-2xl text-center mb-16">
              <h2 className="mb-4 text-3xl font-bold tracking-tight sm:text-4xl text-gradient">
                Everything you need to stay aligned
              </h2>
              <p className="text-lg text-muted-foreground">
                Built for teams that want AI-first tools for managing work.
              </p>
            </div>
          </ScrollAnimation>

          <div className="grid gap-8 md:grid-cols-3">
            {features.map((feature, index) => (
              <ScrollAnimation key={feature.title} delay={index * 0.1}>
                <div
                  className="group relative rounded-2xl border border-white/10 bg-surface-elevated/50 backdrop-blur-xl p-8 transition-all duration-500 hover:border-primary/50 hover:shadow-glow-sm hover:-translate-y-1 overflow-hidden h-full"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                  <div className="relative z-10">
                    <div className="mb-6 inline-flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10 text-primary transition-all duration-300 group-hover:scale-110 group-hover:bg-primary group-hover:text-primary-foreground shadow-sm">
                      <feature.icon className="h-7 w-7" />
                    </div>
                    <h3 className="mb-3 text-xl font-semibold group-hover:text-primary transition-colors">{feature.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              </ScrollAnimation>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 overflow-hidden relative">
        <div className="absolute inset-0 bg-surface -skew-y-3 transform origin-top-left scale-110 -z-10" />
        <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-primary/5 blur-[100px] rounded-full -z-10" />

        <div className="container mx-auto px-4">
          <div className="grid items-center gap-16 lg:grid-cols-2">
            <div className="order-2 lg:order-1 relative mx-auto w-full max-w-[500px] lg:max-w-none">
              <div className="relative rounded-2xl border border-white/20 bg-white/5 p-2 backdrop-blur-2xl shadow-2xl ring-1 ring-white/10">
                <div className="rounded-xl border border-white/10 bg-card/80 overflow-hidden backdrop-blur-sm">
                  {/* Mock Window Header */}
                  <div className="flex items-center gap-2 border-b border-white/5 bg-muted/30 px-4 py-3">
                    <div className="flex gap-1.5">
                      <div className="h-2.5 w-2.5 rounded-full bg-red-400/80" />
                      <div className="h-2.5 w-2.5 rounded-full bg-amber-400/80" />
                      <div className="h-2.5 w-2.5 rounded-full bg-green-400/80" />
                    </div>
                    <div className="ml-4 h-5 w-40 rounded-full bg-muted/50" />
                  </div>

                  {/* Mock Content */}
                  <div className="p-6 space-y-6">
                    {/* Bot Message */}
                    <div className="flex gap-4">
                      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white shadow-lg shrink-0">
                        <Sparkles className="h-5 w-5" />
                      </div>
                      <div className="flex-1 space-y-3">
                        <div className="rounded-2xl rounded-tl-sm bg-muted/50 p-4 shadow-sm">
                          <div className="h-2.5 w-3/4 rounded bg-foreground/10 mb-2 animate-pulse" />
                          <div className="h-2.5 w-1/2 rounded bg-foreground/10 animate-pulse" />
                        </div>
                      </div>
                    </div>

                    {/* User Message */}
                    <div className="flex gap-4 flex-row-reverse">
                      <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center shrink-0">
                        <Users className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <div className="rounded-2xl rounded-tr-sm bg-primary p-4 shadow-glow-sm text-primary-foreground transform transition-transform hover:scale-[1.02]">
                        <p className="text-sm font-medium">What are my top priorities for this week?</p>
                      </div>
                    </div>

                    {/* Bot analysis */}
                    <div className="flex gap-4">
                      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white shadow-lg shrink-0">
                        <Sparkles className="h-5 w-5" />
                      </div>
                      <div className="flex-1">
                        <div className="rounded-2xl rounded-tl-sm bg-muted/50 p-4 shadow-sm space-y-3">
                          <p className="text-sm font-medium text-foreground">Here are your top 3 Strategic High Priorities:</p>
                          <div className="space-y-2">
                            <div className="flex items-center gap-3 bg-background/50 p-2.5 rounded-lg border border-border/50 hover:bg-background transition-colors">
                              <div className="h-2.5 w-2.5 rounded-full bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.5)]" />
                              <div className="h-2 w-32 rounded bg-foreground/10" />
                            </div>
                            <div className="flex items-center gap-3 bg-background/50 p-2.5 rounded-lg border border-border/50 hover:bg-background transition-colors">
                              <div className="h-2.5 w-2.5 rounded-full bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.5)]" />
                              <div className="h-2 w-24 rounded bg-foreground/10" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Element */}
              <div className="absolute -bottom-6 -left-6 rounded-xl border border-white/20 bg-card/90 p-4 shadow-xl animate-float backdrop-blur-md">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-success/10 text-success">
                    <TrendingUp className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">Efficiency</p>
                    <p className="text-lg font-bold text-foreground">Trending Up</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 rounded-lg bg-primary/10 px-3 py-1 text-sm font-medium text-primary mb-6">
                <Zap className="h-4 w-4" />
                <span>AI Intelligence</span>
              </div>
              <h2 className="mb-6 text-4xl font-bold tracking-tight sm:text-5xl leading-tight">
                AI that understands <br />
                <span className="text-gradient">your context</span>
              </h2>
              <p className="mb-8 text-xl text-muted-foreground leading-relaxed">
                Our AI doesn't just answer questions—it understands your priorities,
                team structure, and progress to provide meaningful insights tailored to your organization.
              </p>
              <ul className="space-y-5">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3 group">
                    <CheckCircle2 className="h-6 w-6 flex-shrink-0 text-primary mt-0.5 transition-transform group-hover:scale-110" />
                    <span className="text-foreground font-medium group-hover:text-primary transition-colors">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="border-t border-border bg-gradient-to-br from-primary/5 via-background to-accent/5 py-32">
        <div className="container mx-auto px-4 text-center">
          <div className="mx-auto max-w-2xl">
            <h2 className="mb-6 text-4xl font-bold tracking-tight sm:text-5xl">
              Ready to transform how your team works?
            </h2>
            <p className="mb-10 text-xl text-muted-foreground">
              Join forward-thinking teams using AI to stay aligned, eliminate busy work, and move faster than ever.
            </p>
            <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link to="/auth">
                <Button variant="hero" size="xl" className="shadow-xl shadow-primary/20 w-full sm:w-auto">
                  <span className="flex items-center gap-2">
                    Get Started Now
                    <ArrowRight className="h-5 w-5" />
                  </span>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-surface-elevated pt-16 pb-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 gap-8 md:grid-cols-4 lg:grid-cols-5 mb-16">
            <div className="col-span-2 lg:col-span-2">
              <Link to="/" className="flex items-center gap-3 mb-6">
                <img
                  src="/infina-logo.png"
                  alt="Infina1t Logo"
                  className="h-8 w-auto object-contain"
                />
              </Link>
              <p className="max-w-xs text-muted-foreground mb-6">
                The AI-native priority management platform for modern teams.
                Stay aligned, track progress, and achieve your goals.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-foreground mb-6">Product</h4>
              <ul className="space-y-4 text-sm text-muted-foreground">
                <li><Link to="#" className="hover:text-primary transition-colors">Features</Link></li>
                <li><Link to="#" className="hover:text-primary transition-colors">Pricing</Link></li>
                <li><Link to="#" className="hover:text-primary transition-colors">Changelog</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-foreground mb-6">Company</h4>
              <ul className="space-y-4 text-sm text-muted-foreground">
                <li><Link to="#" className="hover:text-primary transition-colors">About</Link></li>
                <li><Link to="#" className="hover:text-primary transition-colors">Blog</Link></li>
                <li><Link to="#" className="hover:text-primary transition-colors">Contact</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-foreground mb-6">Legal</h4>
              <ul className="space-y-4 text-sm text-muted-foreground">
                <li><Link to="#" className="hover:text-primary transition-colors">Privacy</Link></li>
                <li><Link to="#" className="hover:text-primary transition-colors">Terms</Link></li>
                <li><Link to="#" className="hover:text-primary transition-colors">Security</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-border pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} Infina1t Inc. All rights reserved.
            </p>
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-green-500" />
              <span className="text-sm text-muted-foreground">All systems operational</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
